/**
 * La mappa del pannello: una sola, da cui discendono sia il menu sia le rotte.
 *
 * È deliberatamente l'unico posto in cui una schermata viene dichiarata. Il
 * divieto 11 del sistema di progetto vieta «rotte morte, voci di menu che non
 * portano da nessuna parte»: tenere due elenchi — uno per il menu, uno per la
 * navigazione — è il modo in cui quel difetto nasce, perché prima o poi se ne
 * aggiorna uno solo. Qui la voce di menu **è** la rotta. Non possono divergere
 * perché sono lo stesso oggetto.
 *
 * Il campo `permessi` funziona allo stesso modo in tutti e due i versi: la voce
 * compare se chi guarda ha **almeno uno** dei permessi elencati, e la stessa
 * condizione la ricontrolla la guardia di navigazione — perché un indirizzo si
 * può anche scrivere a mano. Resta vero, come sempre, che la sicurezza sta sul
 * server: qui si decide cosa *offrire*, non cosa *permettere*.
 *
 * Un elenco `permessi` vuoto significa "chiunque sia entrato": vale per il
 * cruscotto e per il profilo, non per il resto.
 */

import type { Component } from 'vue';
import type { RouteRecordRaw } from 'vue-router';

/**
 * Il componente di una schermata, senza `null` né `undefined`.
 *
 * `RouteRecordRaw['component']` li ammette entrambi — perché una rotta di solo
 * rimando non ha componente — e con quel tipo l'oggetto costruito dal router non
 * corrisponde più al ramo «rotta con vista» dell'unione, e TypeScript prova a
 * infilarlo in quello del rimando lamentando un `redirect` mancante. Qui una
 * voce di menu senza schermata non esiste, quindi il tipo lo dice.
 */
type Schermata = NonNullable<RouteRecordRaw['component']>;
import {
  Activity,
  PhoneCall,
  Archive,
  CalendarSync,
  Award,
  BellOff,
  BookUser,
  Bug,
  Building2,
  CalendarCheck,
  CalendarDays,
  CalendarClock,
  CalendarPlus,
  CalendarRange,
  Car,
  Clock,
  DoorOpen,
  FileText,
  KeyRound,
  LayoutDashboard,
  MapPin,
  Megaphone,
  Flag,
  MessagesSquare,
  Network,
  Package,
  Presentation,
  Receipt,
  Rows3,
  Scale,
  ScrollText,
  Settings,
  ShieldCheck,
  Shirt,
  Signpost,
  Tags,
  Timer,
  Truck,
  Users,
  Utensils,
  Video,
  Wallet,
  ServerCrash,
} from '@lucide/vue';
import { PERMISSIONS, RoleCode } from '@bestehr/shared';

export interface VoceNav {
  /** Il nome della rotta. Usato ovunque al posto del percorso scritto a mano. */
  name: string;

  /** Il percorso. In italiano, perché lo legge chi lo incolla in una email. */
  path: string;

  /** L'etichetta nel menu e il titolo predefinito della pagina. */
  label: string;

  /** L'icona. Sempre accompagnata dall'etichetta, mai da sola (§12). */
  icon: Component;

  /** Basta **uno** di questi permessi. Vuoto = chiunque sia entrato. */
  permessi: string[];

  /**
   * Oltre ai permessi: un predicato per le voci che non si accendono con un permesso del
   * catalogo ma con una condizione. I «Turni» ne sono l'unico caso: li vede chi è
   * **responsabile di almeno un reparto** (`managedDepartmentIds`) o chi ha `departments:write`
   * — un'appartenenza, non un permesso. Assente = si mostra se i permessi passano.
   */
  visibile?: (auth: {
    managedDepartmentIds: number[];
    hasSubjects: boolean;
    canEvaluate: boolean;
    /** Se in questa sede si fanno spedizioni e ritiri: l'abilitazione, non un permesso. */
    enableLogistics: boolean;
    /** Se questa persona chatta (`enableChat`, gia' risolto da `/auth/me`): la voce «Chat». */
    enableChat: boolean;
    /**
     * Il codice del ruolo (`admin`, `hr`…), per le sole voci che il server chiude **sul ruolo**:
     * oggi le «Chiavi API». `null` finché non si sa chi guarda, e `null` non apre niente.
     */
    roleCode: string | null;
    /** Il server video c'è: senza, «Videochiamate» non si offre (`stores/auth.ts`). */
    videochiamateDisponibili: boolean;
    can: (code: string) => boolean;
  }) => boolean;

  /**
   * `visibile` decide **solo la voce nel menu**, non chi può aprire la rotta: la guardia guarda
   * i soli permessi (`voceApribile`). Oggi lo porta soltanto «Comunicazioni», che è la posta in
   * arrivo di tutti e insieme il compositore di pochi: la voce resta a chi scrive, la pagina si
   * apre dalla campanella a chiunque abbia una bacheca.
   */
  visibileSoloNelMenu?: true;

  /** Il componente della schermata, caricato solo quando serve. */
  component: Schermata;

  /**
   * Rotte figlie che non compaiono nel menu ma appartengono alla voce.
   *
   * Il dettaglio di un dipendente non è una voce di menu, ma quando è aperto la
   * voce "Dipendenti" deve risultare quella corrente: è ciò che dice a chi
   * guarda dove si trova. Il percorso è relativo a quello del genitore.
   */
  figlie?: Array<{
    name: string;
    path: string;
    label: string;
    component: Schermata;
    permessi?: string[];
  }>;
}

export interface SezioneNav {
  /**
   * L'intestazione del gruppo. `null` per le voci che stanno in cima da sole.
   *
   * Le sezioni non si aprono e non si chiudono: sono etichette, non cartelle.
   * Un menu a fisarmonica costringe a due clic per arrivare dove con un elenco
   * piatto se ne fa uno, e nasconde metà del pannello a chi non sa già dove
   * guardare.
   */
  titolo: string | null;
  voci: VoceNav[];
}

/**
 * Chi **amministra** sale, mezzi e calendario — e quindi chi li vede nel Cruscotto.
 *
 * Deciso da Cosimo il 2026-10-03: *«i responsabili di reparto sul backend non devono vedere la
 * parte di prenotazioni»*, e, davanti alla conseguenza, «a tutti, tranne chi le amministra».
 * Prenotare una saletta o un furgone **si fa dall'app**: il Cruscotto è il posto in cui quelle
 * cose si gestiscono — chi le crea, chi le disattiva, chi guarda l'agenda di tutti.
 *
 * I permessi si **sommano** per ruolo e non si sottraggono: non esiste un modo di togliere una
 * voce al solo `manager` lasciandola all'`employee`, perché tutt'e due partono da
 * `EMPLOYEE_BASE`. Quindi la condizione si scrive **in positivo**, qui, una volta per le tre
 * voci, invece di sparpagliare tre elenchi di permessi che un giorno divergono.
 *
 * ⚠ **`CANTEEN_MANAGE` sta qui come segno della reception, e va saputo.** Oggi quel permesso ce
 * l'hanno `reception` e `hr` e nessun altro, quindi la condizione dice il vero; ma è un
 * **indizio**, non il fatto — il giorno che la mensa la gestisse anche qualcun altro, quel
 * qualcuno si troverebbe il Calendario senza averlo chiesto. La cura, quel giorno, è un permesso
 * che nomini la cosa («amministra le prenotazioni»), non un elenco più lungo.
 */
/**
 * Se nello stabilimento di chi guarda **si fanno** spedizioni e ritiri.
 *
 * Non è un permesso: i quattro permessi della logistica adesso ce li ha ogni dipendente
 * (`EMPLOYEE_BASE`), perché Cosimo il 2026-10-03 ha chiesto che *«i ritiri e i luoghi siano
 * creabili da ogni account di ogni persona dal backend, a parte i dipendenti di Gissi»*. La
 * seconda metà di quella frase non è esprimibile con un permesso — chi lavora a Gissi ha lo
 * stesso ruolo `employee` di chi lavora qui — ed è l'abilitazione per sede `enableLogistics`,
 * la settima della famiglia.
 *
 * ⚠ **Questa riga nasconde una voce, non chiude una porta.** A chiuderla c'è
 * `FunzioneAttivaGuard` sui tre controllori della logistica: senza quella, uno che conoscesse
 * l'indirizzo passerebbe comunque. Vale la pena dirlo perché per le sei abilitazioni sorelle
 * quella guardia **non c'era** — e nessuno se n'è accorto per un anno, perché erano accese
 * dappertutto e il buco non si è mai aperto.
 */
const SI_FA_LOGISTICA = (auth: { enableLogistics: boolean }): boolean => auth.enableLogistics;

/**
 * Chi **amministra il sistema**: il ruolo `admin` (l'account `sysadmin`), e nessun altro.
 *
 * È una condizione sul **ruolo** e non su un permesso, ed è la prima del Cruscotto. Il server
 * chiude le chiavi API con `@Roles(RoleCode.ADMIN)` **oltre** a `settings:manage` ([[K675]]):
 * una chiave apre l'anagrafica e i turni di tutta l'azienda a un sistema esterno, ed è un
 * gesto da chi amministra il sistema, non da chi amministra il personale. Cosimo, 2026-10-19:
 * *«fai anche la apposita sezione accessibile solo a sysadmin curatissima»*. Il menu fa la
 * stessa domanda del server, così non offre una porta che poi risponde 403.
 */
const E_SYSADMIN = (auth: { roleCode: string | null }): boolean => auth.roleCode === RoleCode.ADMIN;

const AMMINISTRA_PRENOTAZIONI = (auth: { can: (code: string) => boolean }): boolean =>
  auth.can(PERMISSIONS.MEETING_ROOMS_MANAGE) ||
  auth.can(PERMISSIONS.VEHICLES_MANAGE) ||
  auth.can(PERMISSIONS.CANTEEN_MANAGE) ||
  auth.can(PERMISSIONS.JUSTIFICATIONS_MANAGE);

export const NAV: SezioneNav[] = [
  {
    titolo: null,
    voci: [
      {
        name: 'cruscotto',
        path: '/',
        label: 'Cruscotto',
        icon: LayoutDashboard,
        permessi: [],
        component: () => import('@/views/CruscottoView.vue'),
      },
      {
        /*
          La mensa: il menù della settimana caricato dalle reception, il servizio acceso sede
          per sede, gli ordini del giorno. **Di primo livello, subito sotto Cruscotto** (D34,
          Cosimo 2026-09-14): è una cosa che si usa ogni giorno, e sepolta dentro un'altra voce
          la troverebbero in pochi.

          `canteen:manage` è il permesso della reception, delle Risorse Umane e degli
          amministratori — tre mestieri sotto una chiave sola, ed è la schermata a distribuire
          i gesti per ruolo (il menù lo carica solo la reception, D6; l'instradamento sede →
          reception solo gli amministratori, D4; HR vede il quadro del giorno, D33). Chi ordina
          il proprio pranzo ha `canteen:order:own`, che qui non apre niente: la sua mensa sta
          sull'app, e la vede solo se la sua sede ha il servizio acceso (D2).
        */
        name: 'mensa',
        path: '/mensa',
        label: 'Mensa',
        icon: Utensils,
        permessi: [PERMISSIONS.CANTEEN_MANAGE],
        component: () => import('@/views/mensa/MensaView.vue'),
      },
    ],
  },

  {
    titolo: 'Persone',
    voci: [
      {
        name: 'presenze',
        path: '/presenze',
        label: 'Presenze',
        icon: Clock,
        permessi: [
          PERMISSIONS.WORKTIMES_READ_TEAM,
          PERMISSIONS.WORKTIMES_READ_ALL,
          PERMISSIONS.WORKTIMES_CORRECT,
        ],
        component: () => import('@/views/presenze/PresenzeView.vue'),
      },
      {
        /*
          I turni non hanno un permesso del catalogo: li costruisce chi è **responsabile di un
          reparto** (l'admin lo nomina), e la voce si accende su quell'appartenenza —
          `visibile`, non `permessi`. Un `departments:write` (l'amministrazione) li vede tutti.
          La rotta resta aperta a chi è entrato (`permessi: []`) perché è la schermata a
          scoparsi ai propri reparti: a chi non ne guida nessuno mostra una pagina vuota, non
          un errore, e il menu comunque non gliela offre.
        */
        name: 'turni',
        path: '/turni',
        label: 'Turni',
        icon: CalendarClock,
        permessi: [],
        visibile: (auth) =>
          auth.managedDepartmentIds.length > 0 ||
          // Chi li **guarda** soltanto (l'ufficio del personale, dal 2026-10-08) deve trovare
          // la voce: una sola visione che non si raggiunge dal menu' non esiste.
          auth.can(PERMISSIONS.SHIFTS_READ_ALL) ||
          auth.can(PERMISSIONS.SHIFTS_MANAGE_ALL),
        component: () => import('@/views/organizzazione/TurniView.vue'),
      },
      {
        /*
          Le valutazioni (D43) non hanno un permesso del catalogo, come i turni: l'autorità è
          l'essere **valutatore** (`canEvaluate`, un flag d'account istituito da backend), e la
          voce si accende su quella proprietà — `visibile`, non `permessi`. Chi non lo è non la
          vede, e la rotta resta chiusa dalla guardia (il server la nega comunque). È l'unica
          superficie del Cruscotto riservata a chi valuta: il dipendente valutato non ci entra.
        */
        name: 'valutazioni',
        path: '/valutazioni',
        label: 'Valutazioni',
        icon: Award,
        permessi: [],
        visibile: (auth) => auth.canEvaluate,
        component: () => import('@/views/organizzazione/ValutazioniView.vue'),
      },
      {
        /*
          Voce a sé e non una figlia di «Presenze», benché il percorso stia lì sotto.
          Una figlia non compare nel menu, e questa è una **coda di lavoro**: se non si
          vede, nessuno la apre, e le giornate contese restano ferme fino al giorno di
          paga — che è esattamente il momento in cui non si possono più guardare con
          calma. Il percorso resta annidato perché è dove uno va a cercarla.

          I permessi sono due e non uno. `worktimes:correct` è quello di chi firma; ma
          chi ha la lettura completa deve poter *guardare* la coda anche senza poterla
          chiudere — è la persona che risponde al telefono quando qualcuno chiede
          perché quel martedì gli risulta mezza giornata. Vederla senza poter firmare
          non è un permesso a metà: è il caso normale di chi deve saper rispondere.
          Il pulsante della rettifica, quello, lo accende la schermata guardando
          `worktimes:correct`.

          **E dal 2026-10-06 i permessi sono tre**, su decisione di Cosimo (B7/B6 in
          `da-decidere.md`, racconto in `da-fare.md` §5 septendecies): c'e' anche
          `worktimes:read:team`, cioe' **il responsabile sui suoi**. L'API gliele dava gia'
          — misurato: un responsabile collegato a un sottoposto riceve le sue giornate
          contese — e a nasconderle era soltanto questa riga: il dato c'era, la pagina
          c'era, mancava la porta.

          Il perche', con le parole della decisione: «martedì il badge dice che Rossi e'
          uscito alle 15 e il cartellino alle 18» e' una cosa che **il suo responsabile sa
          spiegare e l'ufficio no** — era in magazzino, era fuori col furgone, aveva il
          badge in tasca. Oggi quella domanda arriva all'ufficio, che deve girarla indietro.

          ⚠ **E va detto che questa riga allarga**, mentre il 3 e il 5 ottobre l'istruzione
          era di **stringere** chi vede dati di presenza (le timbrature nascoste al
          dipendente, [[K431]]). Non e' una contraddizione ed e' per questo che non si e'
          fatta da sole: la' si toglieva a chi non doveva vedere **i propri**, qui si da' a
          chi deve spiegare **quelli dei suoi**. Se un domani sembrera' incoerente, la
          ragione sta qui.
        */
        name: 'divergenze',
        path: '/presenze/divergenze',
        label: 'Divergenze badge',
        icon: Scale,
        permessi: [
          PERMISSIONS.WORKTIMES_CORRECT,
          PERMISSIONS.WORKTIMES_READ_ALL,
          PERMISSIONS.WORKTIMES_READ_TEAM,
        ],
        component: () => import('@/views/presenze/DivergenzeView.vue'),
      },
      {
        /*
          L'occhio d'insieme sulle presenze — «la settimana scorsa chi ha lavorato quanto» —
          che il riepilogo per-persona non dà. Sola lettura, `worktimes:read:all`: chi vede
          le ore di tutti le può vedere anche sommate su una tabella. Da qui ci si accorge di
          chi andare a guardare; il dettaglio sta nel riepilogo della persona.
        */
        name: 'ore-lavorate',
        path: '/presenze/ore',
        label: 'Ore lavorate',
        icon: Timer,
        permessi: [PERMISSIONS.WORKTIMES_READ_ALL],
        component: () => import('@/views/presenze/RosterView.vue'),
      },
      {
        name: 'assenze',
        path: '/assenze',
        label: 'Assenze',
        icon: CalendarDays,
        permessi: [
          PERMISSIONS.JUSTIFICATIONS_READ_TEAM,
          PERMISSIONS.JUSTIFICATIONS_READ_ALL,
          PERMISSIONS.JUSTIFICATIONS_APPROVE_L1,
          PERMISSIONS.JUSTIFICATIONS_APPROVE_L2,
        ],
        component: () => import('@/views/assenze/AssenzeView.vue'),
      },
      {
        /*
          La funzione dedicata di chi firma le ferie, distinta da «Assenze» (l'elenco di tutte le
          richieste): qui non si consulta, si **decide** — la coda con, sotto gli occhi, la
          copertura del reparto («se approvi, in reparto restano in N») e il calendario delle
          assenze. Compare solo agli approvatori (i due permessi di firma); chi non firma non la
          vede. Le azioni di firma agiscono solo sulle richieste che il server mette in coda a chi
          guarda — quelle di `/justifications/inbox`, già filtrate per firmabilità.
        */
        name: 'approvazione-ferie',
        path: '/assenze/approvazione',
        label: 'Approvazione ferie',
        icon: CalendarCheck,
        // Non un permesso, ma **avere davvero dei sottoposti**: l'autorità di firma viene
        // dall'essere approvatore (`approvator1/2`) o capo di reparto, non da un permesso del
        // catalogo — un approvatore normale (ruolo `employee`) non ha `approve:l1/l2` eppure
        // deve vedere la coda, e un amministratore senza sottoposti non deve vederla. È lo
        // stesso `hasSubjects` (= `subjectsOf` non vuoto) che filtra la coda lato server.
        permessi: [],
        visibile: (auth) => auth.hasSubjects,
        component: () => import('@/views/assenze/ApprovazioneFerieView.vue'),
      },
      {
        /*
          La griglia persona × giorno che il vecchio aveva e il nuovo aveva perso: si guarda
          per colonne — «quel giorno in quel reparto chi c'è» — dove «Assenze» è l'elenco
          delle richieste e «Monteore» è una persona alla volta. Il permesso è la lettura
          completa: chi vede tutte le assenze può vederle disposte a calendario. I tipi
          sensibili arrivano già velati dal server (chi è assente e quando, non perché).
        */
        name: 'prospetto-ferie',
        path: '/assenze/prospetto',
        label: 'Prospetto ferie',
        icon: CalendarRange,
        permessi: [PERMISSIONS.JUSTIFICATIONS_READ_ALL],
        component: () => import('@/views/assenze/ProspettoFerieView.vue'),
      },
      {
        /*
          Il verso opposto della richiesta: qui HR propone un periodo e il dipendente accetta
          dall'app. L'API (`/proposals`) e il lato dipendente (mobile) c'erano; mancava la
          schermata da cui proporre. Permesso `proposals:manage` — proporre e ritirare stanno
          più in alto del rispondere, che è di ogni dipendente.
        */
        name: 'proposte-ferie',
        path: '/assenze/proposte',
        label: 'Proposte ferie',
        icon: CalendarPlus,
        permessi: [PERMISSIONS.PROPOSALS_MANAGE],
        component: () => import('@/views/assenze/ProposteView.vue'),
      },
      {
        /*
          Sta accanto ad «Assenze» e non dentro «Sistema» perché non è una pagina di
          manutenzione: è la coda di lavoro di chi risponde delle assenze. Il permesso
          è quello della lettura completa e non `justifications:manage`, perché la
          pagina non decide niente — mostra chi è fermo e da quanto — e chi ha il
          titolo per vedere tutte le richieste ha già il titolo per vedere quali
          aspettano. Il pulsante che manda i solleciti, quello sì, si offre solo a chi
          amministra, e a deciderlo è il catalogo dei lavori sul server.
        */
        name: 'firme-in-ritardo',
        path: '/assenze/firme-ferme',
        label: 'Firme ferme',
        icon: Timer,
        permessi: [PERMISSIONS.JUSTIFICATIONS_READ_ALL],
        component: () => import('@/views/assenze/FirmeInRitardoView.vue'),
      },
      {
        name: 'monteore',
        path: '/monteore',
        label: 'Monteore',
        icon: Wallet,
        permessi: [PERMISSIONS.LEAVE_BALANCES_MANAGE],
        component: () => import('@/views/assenze/MonteoreView.vue'),
      },
      {
        /*
          I tipi di assenza (ferie, malattia, permesso…) erano scolpiti nel seme: cambiarne colore,
          firme o velatura GDPR richiedeva un rilascio. Qui li si configura.

          **`settings:manage` e non `justifications:manage`** — «VISIBILI SOLO ALL'ADMIN», deciso da
          Cosimo il 2026-09-07 per tutte e tre le viste di configurazione dei tipi (assenze, note
          spese, contenitori). Non si è inventato un permesso nuovo: `settings:manage` è già nel
          catalogo, sta nel gruppo «Sistema» e **non è in nessun pacchetto di ruolo** (`admin` lo ha
          per il bypass da superuser, e nessun altro), quindi «solo admin» è esattamente quel che
          esprime — la stessa scelta della vista dei crash, che riusa `audit:read:auth` invece di
          coniare una chiave nuova.

          Qui si accende la **voce di menu e la rotta** (chi può *aprire* la schermata); i **pulsanti**
          dentro la vista restano su `justifications:manage`, che è il permesso che il server chiede
          davvero alle rotte di scrittura (`justification-types.controller.ts`) e che l'admin
          soddisfa da superuser. Non sono lo stesso permesso di proposito, e la direzione conta: la
          porta è più stretta del pulsante, mai il contrario, così non si ripete il difetto pagato
          due volte (menu che offre e server che poi rifiuta con un 403 letto come «vuoto»). Il
          permesso della rotta di scrittura **non** si è spostato su `settings:manage`: lo consulta
          solo quel controller, spostarlo lo orfanerebbe e `check:permessi` diventerebbe rosso, e
          toccare `permissions.ts` è fuori corsia.
        */
        name: 'tipi-assenza',
        path: '/assenze/tipi',
        label: 'Tipi di assenza',
        icon: Tags,
        permessi: [PERMISSIONS.SETTINGS_MANAGE],
        component: () => import('@/views/assenze/TipiAssenzaView.vue'),
      },
      {
        name: 'buste-paga',
        path: '/buste-paga',
        label: 'Buste paga',
        icon: FileText,
        permessi: [PERMISSIONS.PAYCHECKS_MANAGE],
        component: () => import('@/views/paghe/BustePagaView.vue'),
      },
      {
        name: 'divise',
        path: '/divise',
        label: 'Divise',
        icon: Shirt,
        permessi: [PERMISSIONS.UNIFORMS_MANAGE],
        component: () => import('@/views/divise/DiviseView.vue'),
      },
      {
        /*
          Lo strato di policy che il vecchio aveva e il nuovo aveva perso: quali capi ogni
          reparto può chiedere (per genere), i gruppi col loro tetto di quantità, e il foglio
          taglie con cui il magazzino ordina. Sta accanto a «Divise» — l'elenco e le code delle
          richieste — perché è la stessa gente che le amministra, `uniforms:manage`.
        */
        name: 'divise-reparti',
        path: '/divise/reparti',
        label: 'Divise per reparto',
        icon: ShieldCheck,
        permessi: [PERMISSIONS.UNIFORMS_MANAGE],
        component: () => import('@/views/divise/DiviseRepartiView.vue'),
      },
    ],
  },

  {
    titolo: 'Spese',
    voci: [
      {
        name: 'note-spese',
        path: '/note-spese',
        label: 'Note spese',
        icon: Receipt,
        permessi: [PERMISSIONS.EXPENSES_REVIEW, PERMISSIONS.EXPENSES_MANAGE],
        component: () => import('@/views/spese/NoteSpeseView.vue'),
      },
      {
        name: 'trasferte',
        path: '/trasferte',
        label: 'Trasferte',
        icon: MapPin,
        permessi: [PERMISSIONS.BUSINESS_TRIPS_MANAGE],
        component: () => import('@/views/spese/TrasferteView.vue'),
      },
      {
        /* Le voci di una nota spese (pasti, alloggio…) col loro tetto: erano nel seme, qui si
           configurano. `settings:manage` — «solo admin» (Cosimo, 2026-09-07): il perché per esteso
           sta su «Tipi di assenza». I pulsanti dentro la vista restano su `expenses:manage`, che è
           quel che chiede il server (`expense-note-types.controller.ts`) e che l'admin ha da
           superuser; la voce di menu è più stretta di loro, mai il contrario. */
        name: 'tipi-spesa',
        path: '/spese/tipi',
        label: 'Tipi di nota spese',
        icon: Tags,
        permessi: [PERMISSIONS.SETTINGS_MANAGE],
        component: () => import('@/views/spese/TipiSpesaView.vue'),
      },
    ],
  },

  {
    titolo: 'Logistica',
    voci: [
      {
        name: 'spedizioni',
        path: '/spedizioni',
        label: 'Spedizioni',
        icon: Package,
        permessi: [PERMISSIONS.LOGISTICS_TRANSFERS_READ],
        visibile: SI_FA_LOGISTICA,
        component: () => import('@/views/logistica/SpedizioniView.vue'),
      },
      {
        name: 'viaggi',
        path: '/viaggi',
        label: 'Viaggi',
        icon: Truck,
        permessi: [PERMISSIONS.LOGISTICS_TRIPS_READ],
        visibile: SI_FA_LOGISTICA,
        component: () => import('@/views/logistica/ViaggiView.vue'),
        // Il costruttore non è una voce di menu a sé: ci si arriva dal tasto «Costruisci un
        // viaggio» dentro i Viaggi. È una rotta figlia perché, aperta, la voce corrente resti
        // «Viaggi». Il permesso fine (pianificazione) lo controlla la schermata.
        figlie: [
          {
            name: 'viaggi-nuovo',
            path: '/viaggi/nuovo',
            label: 'Costruttore Viaggi',
            component: () => import('@/views/logistica/CostruttoreViaggiView.vue'),
          },
        ],
      },
      {
        name: 'luoghi',
        path: '/luoghi',
        label: 'Luoghi',
        icon: MapPin,
        permessi: [PERMISSIONS.LOGISTICS_LOCATIONS_READ],
        visibile: SI_FA_LOGISTICA,
        component: () => import('@/views/logistica/LuoghiView.vue'),
      },
      {
        /* Le forme in cui la merce viaggia (busta, scatola, bancale), con misura e peso: erano
           testo libero nel vecchio, qui sono numeri configurabili. `settings:manage` — «solo admin»
           (Cosimo, 2026-09-07): il perché per esteso sta su «Tipi di assenza». I pulsanti dentro la
           vista restano su `logistics:package-types:manage`, che è quel che chiede il server
           (`locations.controller.ts`) e che l'admin ha da superuser; la voce di menu è più stretta
           di loro, mai il contrario. */
        name: 'tipi-contenitore',
        path: '/logistica/contenitori',
        label: 'Tipi di contenitore',
        icon: Tags,
        permessi: [PERMISSIONS.SETTINGS_MANAGE],
        visibile: SI_FA_LOGISTICA,
        component: () => import('@/views/logistica/TipiContenitoreView.vue'),
      },
    ],
  },

  {
    /*
      Si chiamava «Prenotazioni», e il nome era parte di un difetto vero.

      Convocare una riunione dal Cruscotto si poteva già — provato: `POST /calendar/events` con
      `type: 'MEETING'` e gli invitati risponde 201, e l'invito arriva — ma stava dentro
      «Prenotazioni → Calendario», dietro un pulsante che diceva «Nuova prenotazione». Cosimo
      non l'ha trovata, e quando gliel'ho indicata ha risposto: «**Tiralo fuori. Non lo voglio
      lì**» (2026-10-03).

      «Agenda» tiene insieme riunioni, calendario, mezzi e sale **senza dire che si prenota**:
      una riunione si convoca, e la sala è un mezzo della riunione, per giunta facoltativo
      (si fa anche senza). Un contenitore non può essere la cosa contenuta solo a volte.
    */
    titolo: 'Agenda',
    voci: [
      {
        /*
          Prima del Calendario, e non dentro: è la voce che si cerca più spesso.

          `Presentation` — la lavagna sul cavalletto — e non `Users`: due omini sarebbero la
          stessa sagoma di «Dipendenti», tre voci più in giù, e due icone uguali nello stesso
          menu si confondono con la coda dell'occhio. Né `CalendarDays`, che direbbe
          «calendario» proprio accanto al Calendario.

          Il permesso è quello di **lettura**: anche un invitato senza `CALENDAR_WRITE` deve
          poter aprire la pagina e rispondere. A difendere la convocazione è il pulsante, che
          compare solo a chi può scrivere — e il server, che resta l'unico che decide.
        */
        name: 'riunioni',
        path: '/riunioni',
        label: 'Riunioni',
        icon: Presentation,
        permessi: [PERMISSIONS.CALENDAR_READ_OWN, PERMISSIONS.CALENDAR_READ_ALL],
        component: () => import('@/views/agenda/RiunioniView.vue'),
        // Convocare non è una voce di menu a sé: ci si arriva dal pulsante dentro Riunioni, e
        // la voce corrente deve restare «Riunioni» mentre si compila. Stessa forma del
        // costruttore dei viaggi.
        figlie: [
          {
            /*
              ⚠ **Non è più una pagina: porta al quadro di «Riunioni».** Dal 2026-10-15 la
              convocazione vive dentro `/riunioni?convoca`, per ordine di Cosimo (*«falle
              diventare una»*). L'indirizzo resta perché lo conoscono i segnalibri e i rimandi
              scritti prima, e chi lo apre non si accorge di niente: il perché per esteso sta
              in `VaiAConvocare.vue`.
            */
            name: 'riunioni-nuova',
            path: '/riunioni/nuova',
            label: 'Convoca una riunione',
            component: () => import('@/views/agenda/VaiAConvocare.vue'),
          },
        ],
      },
      {
        /*
          Le videochiamate (2026-10-26, `spec/videochiamate.md`): le mie, in corso e programmate,
          «Nuova chiamata», e la scheda col link. La stanza vera sta fuori dalla cornice
          (`/chiamata/:codice`, nel router), come l'ospite e la console di sala: qui c'è solo
          la porta del menu. Lo stesso permesso di lettura delle Riunioni: chi può vedere la
          propria agenda può convocare una chiamata, e Cosimo la vuole per tutto il personale.
        */
        name: 'videochiamate',
        path: '/videochiamate',
        label: 'Videochiamate',
        icon: Video,
        permessi: [PERMISSIONS.CALENDAR_READ_OWN, PERMISSIONS.CALENDAR_READ_ALL],
        // Solo se il server video c'è: in produzione oggi non c'è, e il Cruscotto esce prima.
        visibile: (auth) => auth.videochiamateDisponibili,
        component: () => import('@/views/videochiamate/VideochiamateView.vue'),
      },
      {
        name: 'calendario',
        path: '/calendario',
        label: 'Calendario',
        icon: CalendarDays,
        permessi: [PERMISSIONS.CALENDAR_READ_OWN, PERMISSIONS.CALENDAR_READ_ALL],
        visibile: AMMINISTRA_PRENOTAZIONI,
        component: () => import('@/views/prenotazioni/CalendarioView.vue'),
      },
      {
        name: 'mezzi',
        path: '/mezzi',
        label: 'Mezzi',
        icon: Car,
        permessi: [PERMISSIONS.VEHICLES_READ],
        visibile: AMMINISTRA_PRENOTAZIONI,
        component: () => import('@/views/prenotazioni/MezziView.vue'),
      },
      {
        name: 'sale',
        path: '/sale',
        label: 'Sale riunioni',
        icon: Building2,
        permessi: [PERMISSIONS.MEETING_ROOMS_READ],
        visibile: AMMINISTRA_PRENOTAZIONI,
        component: () => import('@/views/prenotazioni/SaleView.vue'),
      },
      {
        name: 'cartelli',
        path: '/cartelli',
        label: 'Cartelli e-ink',
        icon: Signpost,
        // `manage` e non `read`: la voce sopra — «Sale riunioni» — la vede chi prenota, ed
        // è giusto. Questa no: è un inventario di manutenzione (targhette, firmware,
        // batterie, chi tace) e ogni bottone della pagina vuole `manage`. Con `read` ci
        // finiva dentro ogni dipendente, per trovarci un 403 su qualunque cosa toccasse.
        permessi: [PERMISSIONS.MEETING_ROOMS_MANAGE],
        component: () => import('@/views/prenotazioni/CartelliView.vue'),
      },
    ],
  },

  {
    titolo: 'Comunicazione',
    voci: [
      {
        /* La messaggistica interna, che viveva solo sull'app. Qui a ricarichi ravvicinati (il
           pannello non ha il socket dell'app). `chat:use`, come sull'app. */
        name: 'chat',
        path: '/chat',
        label: 'Chat',
        icon: MessagesSquare,
        permessi: [PERMISSIONS.CHAT_USE],
        // E l'abilitazione, come sull'app: con la chat spenta il server risponde 403 a ogni
        // rotta della chat (`@PretendeLaFunzione('enableChat')`), e la voce porterebbe li'.
        visibile: (auth) => auth.enableChat,
        component: () => import('@/views/comunicazione/ChatView.vue'),
      },
      {
        /*
          Le segnalazioni di chat (A8, K277): dove l'ufficio del personale **legge** quello
          che l'avviso «Un messaggio segnalato» gli ha annunciato. Sta sotto Comunicazione e
          non sotto Sistema perché non è diagnostica: è gente che parla con gente.

          `CHAT_MODERATE` e non `CHAT_USE`: la riga porta il testo di un messaggio privato e
          i nomi di due persone — chi ha segnalato e chi è stato segnalato —, e chi può
          usare la chat non per questo può leggere le chat degli altri.
        */
        name: 'segnalazioni',
        path: '/segnalazioni',
        label: 'Segnalazioni',
        icon: Flag,
        permessi: [PERMISSIONS.CHAT_MODERATE],
        component: () => import('@/views/comunicazione/SegnalazioniView.vue'),
      },
      {
        name: 'comunicazioni',
        path: '/comunicazioni',
        label: 'Comunicazioni',
        icon: Megaphone,
        /**
         * Due condizioni diverse, perché questa pagina è due cose: **la posta in arrivo** di
         * chi la apre e **il compositore** delle circolari.
         *
         * La rotta si apre a chi può leggere le proprie notifiche, cioè a chiunque — ed è la
         * riparazione di un difetto che stava lì da prima: la campanella della barra in alto
         * punta qui (`AppTopbar`, `{ name: 'comunicazioni' }`), e con l'ingresso legato al
         * permesso di *invio* portava a «non autorizzato» tutti i 486 dipendenti che una
         * casella ce l'hanno eccome. Il difetto è diventato urgente il giorno in cui il ruolo
         * `logistica` ha smesso di poter scrivere (decisione di Cosimo del 2026-09-06): due
         * account che vivono di avvisi in arrivo si sarebbero ritrovati senza bacheca sul
         * pannello, per una decisione che riguardava l'uscita e non l'entrata.
         *
         * La **voce nel menu** invece resta a chi scrive: `visibile` la guarda, e la barra
         * laterale la legge (il router no, di proposito). Chi non manda circolari trova la
         * propria posta dalla campanella, che è dove la cerca; allungare a tutti il menu del
         * Cruscotto sarebbe una scelta d'interfaccia che nessuno ha chiesto.
         *
         * Dentro la pagina il compositore è già suo: il bottone «Scrivi» sta dietro
         * `puoInviare`, e il rapporto di consegna dietro «l'ho scritta io».
         */
        permessi: [PERMISSIONS.NOTIFICATIONS_READ_OWN],
        visibile: (auth) => auth.can(PERMISSIONS.NOTIFICATIONS_SEND_TEAM),
        /*
          ⚠ Il «router no» qui sopra era diventato falso il 2026-10-14, quando la guardia ha
          cominciato a chiedere `voceVisibile` — che legge anche `visibile` — e la campanella
          era tornata a portare a «non autorizzato» chiunque non mandi circolari (K416 rotto da
          un'altra parte). Questo è il segno che lo rende vero di nuovo.
        */
        visibileSoloNelMenu: true,
        component: () => import('@/views/comunicazione/ComunicazioniView.vue'),
      },
      {
        name: 'rubrica',
        path: '/rubrica',
        label: 'Rubrica',
        icon: BookUser,
        permessi: [PERMISSIONS.CONTACTS_READ],
        component: () => import('@/views/comunicazione/RubricaView.vue'),
      },
    ],
  },

  {
    titolo: 'Organizzazione',
    voci: [
      {
        name: 'dipendenti',
        path: '/dipendenti',
        label: 'Dipendenti',
        icon: Users,
        /**
         * Nel menu la vede **chi l'anagrafica la scrive**, non chi la legge.
         *
         * Cosimo, 2026-10-14: *«Comunicazione, dipendenti e sedi e reparti toglili dai
         * dipendenti normali e dagli approvatori»*.
         *
         * ⚠ **`users:read` non si tocca, e la ragione è una sua decisione di undici giorni
         * prima.** D59, 2026-10-03, qui sopra per esteso: *«sì, a tutti i dipendenti»*, perché
         * convocare una riunione comincia con lo scegliere chi invitare — e lui ha scelto
         * sapendo che da quel giorno ogni dipendente vede la rubrica aziendale. Una frase che
         * parla del **menù** non revoca una decisione che parlava dei **permessi**: quella si
         * riapre nominandola, non per conseguenza.
         *
         * Quindi: la voce sparisce dal menu di 445 dipendenti e 34 approvatori, la pagina resta
         * raggiungibile da chi ne conosce l'indirizzo, e la scelta se chiuderla davvero sta in
         * `spec/da-decidere.md` §E16 con quel che costa.
         *
         * ⚠ **Qui c'era `visibile: users:write`, ed era rimasto dal 14 ottobre** (laReceptionRiapreIDipendenti,
         * 2026-11-01). Il 16 la porta è passata a `users:directory` proprio perché la reception
         * entrasse, ma la tenda del 14 restava: e dal 14 la guardia chiede `voceVisibile`, che
         * legge anche `visibile`, quindi la reception — che quel permesso ce l'ha — finiva su
         * «non autorizzato» sia dal menù sia dalla scheda di una persona. È esattamente il «una
         * pagina che si chiude a chi ci lavora è rotta» di K589. A dipendenti e approvatori la
         * voce resta chiusa lo stesso: `users:directory` lo hanno solo hr, admin e reception.
         */
        /*
          ⚠ **Chiusa davvero dal 2026-10-16**, e non più solo tolta dal menù.

          Qui c'era `USERS_READ`, che dal 2026-10-03 ce l'hanno tutti: la voce spariva a 445
          dipendenti e la pagina restava aperta a chi ne scriveva l'indirizzo. Cosimo ha
          chiesto di chiuderla il 14, e il 15 notte ha scelto «chiudo anche le pagine, tengo la
          scelta persone».

          Il permesso è nuovo apposta (`users:directory`): chiuderla su `USERS_WRITE` avrebbe
          tagliato fuori la **reception**, che l'anagrafica la usa da agosto col recinto per
          sede, e allargarle `USERS_WRITE` avrebbe aperto ogni recinto che quel permesso legge
          per dire un'altra cosa. Ce l'hanno `hr`, `admin` e `reception`.

          ⚠ `users:read` **non** si tocca: la ricerca con cui si invita qualcuno a una riunione
          continua a funzionare per tutti, ed era la ragione per cui quel permesso era stato
          dato. Chiudere la pagina non chiude il dato, e questa riga non pretende il contrario.
        */
        permessi: [PERMISSIONS.USERS_DIRECTORY],
        component: () => import('@/views/organizzazione/DipendentiView.vue'),
        figlie: [
          {
            name: 'dipendente',
            path: ':id(\\d+)',
            label: 'Scheda dipendente',
            component: () => import('@/views/organizzazione/DipendenteView.vue'),
          },
        ],
      },
      /**
       * L'archivio dei cessati: subito dopo i dipendenti, perché è il loro
       * complemento — di là chi lavora qui, di qua chi ci lavorava.
       *
       * Voce **separata** e non un filtro dell'elenco dei dipendenti, e la
       * separazione è tutta la decisione: `archive:terminated:read` sta solo nel
       * corredo dell'HR, e una casella «mostra anche i cessati» dentro una
       * schermata che si apre con `users:read` sarebbe un permesso che non
       * separa niente. Chi non ha la chiave non vede nemmeno la porta.
       */
      {
        name: 'archivio-cessati',
        path: '/archivio/cessati',
        label: 'Archivio cessati',
        icon: Archive,
        permessi: [PERMISSIONS.ARCHIVE_TERMINATED_READ],
        component: () => import('@/views/organizzazione/ArchivioCessatiView.vue'),
        figlie: [
          {
            name: 'cessato',
            path: ':id(\\d+)',
            label: 'Fascicolo del cessato',
            component: () => import('@/views/organizzazione/FascicoloCessatoView.vue'),
          },
        ],
      },
      {
        name: 'struttura',
        path: '/struttura',
        label: 'Sedi e reparti',
        icon: Network,
        permessi: [PERMISSIONS.DEPARTMENTS_WRITE],
        /**
         * Nel menu la vede **chi le amministra**, non chi le legge.
         *
         * Cosimo, 2026-10-14: *«Comunicazione, dipendenti e sedi e reparti toglili dai
         * dipendenti normali e dagli approvatori»*. Misurato quel giorno: `departments:read` e
         * `headquarters:read` stanno in `EMPLOYEE_BASE`, quindi questa voce la vedevano tutti e
         * 445 i dipendenti e tutti e 34 gli approvatori (su 37; gli altri tre sono `manager`).
         *
         * ⚠ **La condizione sta qui e non nei permessi, e la differenza va saputa.** Togliere
         * `headquarters:read` a `employee` spegnerebbe il nome della sede in cinque schermate
         * della app — prenotazione sale, prenotazione mezzi, inviti — perche' `usaSedi` legge
         * `GET /headquarters` per scrivere «Sala Blu, Colle»; e il suo stesso commento lo
         * dichiara: *«sta in EMPLOYEE_BASE, quindi ce l'hanno tutti — oggi»*. Quindi la rotta
         * resta aperta a chi ne conosce l'indirizzo, ed è una tenda, non una porta. Se la porta
         * si deve chiudere è una decisione che costa quelle cinque schermate, e sta in
         * `spec/da-decidere.md` §E16.
         */
        visibile: (auth) =>
          auth.can(PERMISSIONS.HEADQUARTERS_WRITE) || auth.can(PERMISSIONS.DEPARTMENTS_WRITE),
        /*
          ⚠ **La condizione c'era, e non chiudeva niente**: `headquarters:read` +
          `departments:read`, che stanno in `EMPLOYEE_BASE` — cioè ce l'hanno tutti. Era
          quello che il commento qui sopra chiama «una tenda, non una porta». Chiusa il
          2026-10-16 sulla stessa decisione della rubrica.
          (Questa riga l'avevo scritta dicendo «non c'era nessuna condizione», e non era vero:
          il cancello dei tipi mi ha fermato su un doppione e guardando ho visto la riga che
          c'era già. Una misura che si dà per scontata si scrive sbagliata.)

          `DEPARTMENTS_WRITE` e non un permesso nuovo: questa pagina **cambia** sedi e reparti,
          e chi la apre per guardarli ha già il nome della sede dove gli serve. Ce l'hanno `hr`
          e `admin`, che sono le due che la usano. `headquarters:read` resta a tutti, come
          prima: lo legge `usaSedi` per scrivere «Sala Blu, Colle» in cinque schermate dell'app,
          e toglierlo le spegnerebbe.
        */
        component: () => import('@/views/organizzazione/StrutturaView.vue'),
      },
      {
        /*
          Le linee di produzione: la schermata **da dietro** con cui si riempie la PWA
          «Presenze di linea» (`/linee`, senza cornice, che sta nel router e non qui perché
          non è una voce di menù). Qui si creano le linee, si dice chi ci lavora e chi le
          sorveglia; là le maestre guardano.

          Il percorso sta sotto `/struttura` perché è di quella famiglia — sedi, reparti, e
          adesso linee — e perché `/linee` è già della PWA. `production-lines:manage` è il
          permesso dell'ufficio del personale e degli amministratori: chi conosce
          l'organigramma, perché le assegnazioni cambiano quando qualcuno cambia reparto.
        */
        name: 'linee-produzione',
        path: '/struttura/linee',
        label: 'Linee di produzione',
        icon: Rows3,
        permessi: [PERMISSIONS.PRODUCTION_LINES_MANAGE],
        component: () => import('@/views/organizzazione/LineeView.vue'),
      },
      {
        name: 'ruoli',
        path: '/ruoli',
        label: 'Ruoli e permessi',
        icon: ShieldCheck,
        permessi: [PERMISSIONS.ROLES_READ],
        component: () => import('@/views/organizzazione/RuoliView.vue'),
      },
    ],
  },

  {
    titolo: 'Sistema',
    voci: [
      {
        name: 'automazioni',
        path: '/automazioni',
        label: 'Automazioni',
        icon: Activity,
        permessi: [PERMISSIONS.JOBS_READ],
        component: () => import('@/views/sistema/AutomazioniView.vue'),
      },
      /**
       * I calendari dei telefoni, subito dopo le automazioni e prima dell'archivio ([[K630]]):
       * è l'**effetto** di una di quelle automazioni — il giro può girare regolarmente e
       * riportare zero telefoni, verde di là e rosso di qua. `jobs:read`, come l'archivio, perché
       * la pagina legge; il comando «Rimetti in coda» ha la sua guardia, `settings:manage`, e la
       * pagina non lo disegna a chi risponderebbe 403.
       *
       * Il nome non comincia con «calendario» da solo: in Agenda ci sono già voci col calendario,
       * e due voci che cominciano uguali in due sezioni si aprono a caso.
       */
      {
        name: 'calendari-telefoni',
        path: '/calendari-telefoni',
        label: 'Calendari dei telefoni',
        icon: CalendarSync,
        permessi: [PERMISSIONS.JOBS_READ],
        component: () => import('@/views/sistema/CalendariTelefoniView.vue'),
      },
      /**
       * Chi non riceve le notifiche, subito dopo i calendari dei telefoni ([[K647]]): è la sua
       * sorella, la stessa domanda — «quel che crediamo arrivi sul telefono, ci arriva?» — posta
       * alle notifiche invece che alle agende.
       *
       * `notifications:send:all` e non `_TEAM`, ed è il permesso della rotta (`GET
       * /devices/coverage`, che vuole anche `users:read`, già di chi ha questo): è la pagina di
       * chi scrive a tutta l'azienda e deve sapere a quanti non arriva. Un responsabile con
       * `_TEAM` vedrebbe nome e reparto di quasi cinquecento persone per un permesso che gliene
       * dà venti, e aprirla a lui vorrebbe dire un recinto per reparto: una decisione, non una
       * spunta nel menù.
       */
      {
        name: 'notifiche-telefoni',
        path: '/notifiche-telefoni',
        label: 'Chi non riceve le notifiche',
        icon: BellOff,
        permessi: [PERMISSIONS.NOTIFICATIONS_SEND_ALL],
        /*
          E `users:read`, che la rotta vuole accanto (chiNonRiceveVuoleAncheUsersRead): «già di chi
          ha questo» non è più vero dal 30/10, perché l'interruttore delle comunicazioni dà
          `send:all` a un dipendente che `users:read` non ce l'ha, e la pagina gli rispondeva 403.
        */
        visibile: (auth) => auth.can(PERMISSIONS.USERS_READ),
        component: () => import('@/views/sistema/NotificheTelefoniView.vue'),
      },
      /**
       * L'archivio dei file, subito dopo le automazioni: è la stessa domanda —
       * «il sistema sta ancora facendo il suo lavoro?» — posta sui byte invece
       * che sulle elaborazioni.
       *
       * `jobs:read` e non un permesso proprio, ed è deliberato: questa pagina
       * **legge** e basta, e legge esattamente quello che la verifica notturna
       * scrive. Chi ha il diritto di guardare se le automazioni girano ha il
       * diritto di vedere che cosa hanno trovato; i due comandi in fondo hanno la
       * loro guardia, che è `settings:manage` e la dichiara il catalogo.
       *
       * Il nome è `archivio-file` e non `archivio`: di là c'è già
       * `archivio-cessati`, e due voci che cominciano con la stessa parola in due
       * sezioni diverse sono il genere di somiglianza che fa aprire quella
       * sbagliata.
       */
      {
        name: 'archivio-file',
        path: '/archivio/file',
        label: 'Archivio dei file',
        icon: Archive,
        permessi: [PERMISSIONS.JOBS_READ],
        component: () => import('@/views/sistema/ArchivioView.vue'),
      },
      {
        name: 'registro',
        path: '/registro',
        label: 'Registro attività',
        icon: ScrollText,
        permessi: [PERMISSIONS.AUDIT_READ],
        component: () => import('@/views/sistema/RegistroView.vue'),
      },
      {
        /*
         * La diagnostica dei crash dell'app mobile, accanto al registro perché è la stessa
         * domanda posta sui telefoni: «cosa è andato storto, a chi, e dove». Il telefono manda i
         * crash da sé (`POST /crash/client`, senza permesso — un crash capita a chiunque); qui
         * li si sfoglia.
         *
         * `audit:read:auth` e non `audit:read`, deciso da Cosimo il 2026-09-06: un rapporto
         * porta il ritratto del telefono di una persona con nome e cognome, l'ora in cui stava
         * usando l'app e spesso che cosa stava facendo — la stessa famiglia di dato per cui la
         * sweep legale R4b ha staccato le righe di accesso dal registro. Quel permesso è tenuto
         * fuori da `HR_EXTRA` per scelta dichiarata e non sta in nessun altro pacchetto: oggi ce
         * l'hanno gli Amministratori e nessun altro, che è la decisione alla lettera. Perché si
         * riusa invece di inventarne uno nuovo sta scritto in `apps/api/src/crash`.
         */
        name: 'crash',
        path: '/crash',
        label: 'Crash dell’app',
        icon: Bug,
        permessi: [PERMISSIONS.AUDIT_READ_AUTH],
        component: () => import('@/views/sistema/CrashView.vue'),
      },
      {
        /*
          Gli errori del **server**, fratelli di quelli del telefono qui sopra e con lo stesso
          permesso, per la stessa ragione: un errore porta il ritratto di che cosa stava facendo il
          sistema, spesso con dentro percorsi di file e messaggi del database.

          Nasce il 2026-09-30 su richiesta di Cosimo — «unifica tutto ed evitiamo di avere un altro
          accesso» — per non dover entrare in un secondo cruscotto. L'archivio resta fuori dal
          processo (il perche' sta in `apps/api/src/crash/errori-server.service.ts`); qui cambia
          dove si guarda.
        */
        name: 'errori-server',
        path: '/errori-server',
        label: 'Errori del server',
        icon: ServerCrash,
        permessi: [PERMISSIONS.AUDIT_READ_AUTH],
        component: () => import('@/views/sistema/ErroriServerView.vue'),
      },
      {
        name: 'impostazioni',
        path: '/impostazioni',
        label: 'Impostazioni',
        icon: Settings,
        permessi: [PERMISSIONS.SETTINGS_READ],
        component: () => import('@/views/sistema/ImpostazioniView.vue'),
      },
      {
        /*
          Il Centralino di Beste Talk (2026-11-02): gli interni persona per persona, le chiamate col
          loro registro passo per passo, i telefoni che squillano. `telephony:manage`: l'amministratore
          e chi ha «Gestisce il centralino» nella scheda del dipendente.
        */
        name: 'centralino',
        path: '/centralino',
        label: 'Centralino',
        icon: PhoneCall,
        permessi: [PERMISSIONS.TELEPHONY_MANAGE],
        component: () => import('@/views/telefonia/CentralinoView.vue'),
      },
      {
        /*
          La qualità delle videochiamate (2026-10-28), il cruscotto scelto da Cosimo «lato sysadmin,
          in modo che ci siano le informazioni da interrogare»: chi c'era, da dove, come è uscito e
          com'era la sua linea. **Solo sysadmin**, come «Porte» qui sotto e per la stessa ragione:
          il server (`QualitaController`) pretende `RoleCode.ADMIN` oltre a `settings:manage`.
        */
        name: 'qualita-chiamate',
        path: '/qualita-chiamate',
        label: 'Qualità delle chiamate',
        icon: Activity,
        permessi: [PERMISSIONS.SETTINGS_MANAGE],
        visibile: E_SYSADMIN,
        component: () => import('@/views/sistema/QualitaChiamateView.vue'),
      },
      {
        /*
          Le porte che si aprono col telefono ([[K741]]): le centraline ESP32 con lettore NFC
          davanti alle sale, il collegamento MQTT con cui BesteHR gli manda l'elenco delle chiavi
          valide e ne riceve gli accessi, e il **registro degli accessi** di ogni porta. Cosimo:
          *«DEVI AVERE TE POI LE INFORMAZIONI DI ACCESSO DELLE SALE E LA GESTIONE DELLE
          AUTORIZZAZIONI»*. Le autorizzazioni — chi apre quale porta — stanno nella scheda del
          dipendente; qui stanno le porte e chi ci è passato.

          **Solo sysadmin**, come «Chiavi API» qui sotto e per la stessa ragione: il server
          (`PorteController`) pretende `RoleCode.ADMIN` oltre a `settings:manage`, e la voce fa
          la stessa domanda. Chi entra in una stanza e quando non è una cosa che si mostra in
          giro, e chi può riscrivere i topic di una centralina può decidere chi apre.
        */
        name: 'porte',
        path: '/porte',
        label: 'Porte',
        icon: DoorOpen,
        permessi: [PERMISSIONS.SETTINGS_MANAGE],
        visibile: E_SYSADMIN,
        component: () => import('@/views/sistema/porte/PorteView.vue'),
      },
      {
        /*
          Le chiavi con cui un sistema esterno legge BesteHR ([[K675]]): i servizi (il primo è
          «Beste APS-MES», la pianificazione della produzione), le loro chiavi con ambiti,
          indirizzi e scadenza, e il **registro degli accessi** — quale chiave ha chiamato che
          cosa, quando, da dove, e con che esito. Cosimo: *«Va fatto un menu di gestione delle
          api e delle chiavi con cura assoluta»*, *«voglio vedere anche i log degli ultimi
          accessi e da che chiave sono stati effettuati»*.

          **Solo sysadmin**, ed è la prima voce chiusa su un **ruolo**: `settings:manage` è il
          permesso della rotta, ma il server pretende anche `RoleCode.ADMIN`, e la voce fa la
          stessa domanda (`E_SYSADMIN`). Un segreto non passa mai di qui dopo la nascita: il
          server ne tiene l'impronta, e il pannello lo mostra una volta sola.

          In fondo a «Sistema», dopo le impostazioni: è la cosa che si apre meno spesso e che
          pesa di più quando si apre.
        */
        name: 'chiavi-api',
        path: '/chiavi-api',
        label: 'Chiavi API',
        icon: KeyRound,
        permessi: [PERMISSIONS.SETTINGS_MANAGE],
        visibile: E_SYSADMIN,
        component: () => import('@/views/sistema/chiavi-api/ChiaviApiView.vue'),
        // Il registro degli accessi è una pagina a sé — ha i suoi filtri nell'indirizzo, e un
        // collegamento «registro di questa chiave» deve poterla aprire già filtrata — ma resta
        // una figlia: aperta, nel menu si accende «Chiavi API». Eredita permessi e condizione.
        figlie: [
          {
            name: 'chiavi-api-accessi',
            path: 'accessi',
            label: 'Registro accessi API',
            component: () => import('@/views/sistema/chiavi-api/RegistroAccessiView.vue'),
          },
        ],
      },
    ],
  },
];

/** Tutte le voci, appiattite. Comodo per le guardie e per la ricerca rapida. */
export const VOCI: VoceNav[] = NAV.flatMap((s) => s.voci);

/** Quel che serve per decidere se una voce si vede. È il sottoinsieme che lo store espone. */
export type ChiGuarda = Parameters<NonNullable<VoceNav['visibile']>>[0] & {
  canAny: (...codes: string[]) => boolean;
};

/**
 * Una voce si vede?
 *
 * ⚠ **Esiste perché la risposta stava in due posti e i due posti sono andati a finire
 * diversi.** Cosimo, 2026-10-14, con due fotografie della barra laterale: *«ANCORA CON
 * L'ACCESSO AL BACKEND I RESPONSABILI VEDONO I MENU'»*, e poi *«teoricamente lo vedono anche i
 * dipendenti normali»*. Le tre voci che ha nominato — Comunicazione, Dipendenti, Sedi e reparti
 * — sono state chiuse nel menu lo stesso giorno. **Nel Cruscotto no**: la home ne offriva due
 * come scorciatoie (`Dipendenti` su `users:read`, `Calendario` su `calendar:read:own`), con la
 * loro condizione scritta a mano in un altro file. Due padroni per lo stesso fatto, e il
 * secondo non si è accorto di niente (§116).
 *
 * Adesso la domanda si fa qui, e chiunque la rifaccia — menu, scorciatoie, specchietti —
 * ottiene la stessa risposta. La sua decisione del 2026-10-14, a scelta multipla, è questa:
 * *«Tutti devono poter entrare ma devono vedere e poter fare solo quello per cui sono
 * abilitati»* — quindi niente porta sul Cruscotto, e ogni voce si accende su quel che quella
 * persona può davvero fare.
 *
 * Un nome che non è di nessuna voce torna `false` e **non** solleva: chi chiama sta decidendo
 * se disegnare un collegamento, e in dubbio non lo disegna. Che i nomi esistano lo tiene il
 * cancello `pnpm check:scorciatoie`, che è il posto giusto perché un nome scritto male è un
 * difetto di scrittura, non una condizione a tempo d'esecuzione.
 */
export function voceVisibile(nome: string, auth: ChiGuarda): boolean {
  const trovata = cerca(nome);
  if (!trovata) return false;
  const { permessi, visibile } = trovata;
  if (permessi.length > 0 && !auth.canAny(...permessi)) return false;
  return visibile?.(auth) ?? true;
}

/**
 * La rotta di questa voce si apre? È la domanda della guardia, e coincide con `voceVisibile`
 * tranne per le voci con `visibileSoloNelMenu`, dove la condizione chiude la voce nel menu ma non
 * la pagina, e restano i soli permessi.
 */
export function voceApribile(nome: string, auth: ChiGuarda): boolean {
  const trovata = cerca(nome);
  if (!trovata) return false;
  if (!trovata.visibileSoloNelMenu) return voceVisibile(nome, auth);
  return trovata.permessi.length === 0 || auth.canAny(...trovata.permessi);
}

/**
 * La voce (o la figlia) con questo nome, con le sue due condizioni già risolte.
 *
 * Le figlie si cercano perché sono rotte vere: `/viaggi/nuovo` si raggiunge scrivendola. Le due
 * regole di un'eredità, tutt'e due scritte col perché:
 *
 *  - i **permessi** una figlia li può avere suoi, e se non li ha prende quelli del genitore: il
 *    dettaglio di un dipendente non è più aperto dell'elenco da cui si arriva, e scordarselo
 *    sarebbe il modo silenzioso di renderlo tale;
 *  - la **condizione** è sempre quella del genitore, perché una figlia non ne ha una sua: il
 *    costruttore dei viaggi non è aperto a chi nella sua sede non fa viaggi.
 */
function cerca(nome: string): Pick<VoceNav, 'permessi' | 'visibile' | 'visibileSoloNelMenu'> | null {
  for (const voce of VOCI) {
    if (voce.name === nome) return voce;
    for (const figlia of voce.figlie ?? []) {
      if (figlia.name === nome) {
        return {
          permessi: figlia.permessi ?? voce.permessi,
          visibile: voce.visibile,
          visibileSoloNelMenu: voce.visibileSoloNelMenu,
        };
      }
    }
  }
  return null;
}
